import { cn } from '@/lib/utils';

interface WorkspaceAvatarProps {
  name: string;
  color?: string | null;
  imageUrl?: string | null;
  size?: 'sm' | 'md-sm' | 'md' | 'lg';
  className?: string;
}

const sizeMap = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  'md-sm': 'size-9 text-xs',
  lg: 'size-12 text-base',
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// 20 muted gradient pairs — soft, desaturated tones
const GRADIENT_MAP: Record<string, string> = {
  '#7c8db5': 'linear-gradient(135deg, #7c8db5, #9aaad0)',  // steel blue
  '#8b7fb5': 'linear-gradient(135deg, #8b7fb5, #a99cd0)',  // soft purple
  '#b57f8b': 'linear-gradient(135deg, #b57f8b, #d09ca6)',  // dusty rose
  '#b5897c': 'linear-gradient(135deg, #b5897c, #d0a899)',  // warm clay
  '#b5a07c': 'linear-gradient(135deg, #b5a07c, #d0bb99)',  // sand
  '#a3b57c': 'linear-gradient(135deg, #a3b57c, #bdd099)',  // sage
  '#7cb5a0': 'linear-gradient(135deg, #7cb5a0, #99d0ba)',  // seafoam
  '#7caab5': 'linear-gradient(135deg, #7caab5, #99c5d0)',  // ocean mist
  '#7c95b5': 'linear-gradient(135deg, #7c95b5, #99b0d0)',  // slate blue
  '#9b7cb5': 'linear-gradient(135deg, #9b7cb5, #b699d0)',  // lavender
  '#b57ca3': 'linear-gradient(135deg, #b57ca3, #d099bd)',  // mauve
  '#b5807c': 'linear-gradient(135deg, #b5807c, #d09e99)',  // terracotta
  '#b5ab7c': 'linear-gradient(135deg, #b5ab7c, #d0c699)',  // wheat
  '#8fb57c': 'linear-gradient(135deg, #8fb57c, #aad099)',  // moss
  '#7cb58f': 'linear-gradient(135deg, #7cb58f, #99d0aa)',  // mint
  '#7cb5b5': 'linear-gradient(135deg, #7cb5b5, #99d0d0)',  // teal mist
  '#7c87b5': 'linear-gradient(135deg, #7c87b5, #99a2d0)',  // periwinkle
  '#a87cb5': 'linear-gradient(135deg, #a87cb5, #c399d0)',  // orchid
  '#b57c95': 'linear-gradient(135deg, #b57c95, #d099b0)',  // blush
  '#b59a7c': 'linear-gradient(135deg, #b59a7c, #d0b599)',  // caramel
};

const DEFAULT_GRADIENT = 'linear-gradient(135deg, #94a3b8, #7d8fa8, #a0b1c5)';

function colorToGradient(color: string): string {
  return GRADIENT_MAP[color] || DEFAULT_GRADIENT;
}

export function WorkspaceAvatar({ name, color, imageUrl, size = 'md', className }: WorkspaceAvatarProps) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={cn('rounded-full object-cover shrink-0', sizeMap[size], className)}
      />
    );
  }

  const background = color ? colorToGradient(color) : DEFAULT_GRADIENT;

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-medium text-on-accent shrink-0',
        sizeMap[size],
        className
      )}
      style={{ background }}
    >
      {getInitials(name)}
    </div>
  );
}
